<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { api } from '@/api/client';
import type { PortalSite } from '@/api/types';
import SiteStatusBadge from '@/components/SiteStatusBadge.vue';
import TimeAgo from '@/components/TimeAgo.vue';
import { useUserStore } from '@/stores/user';
import { openRdp } from '@/lib/rdp';

const AUTO_REFRESH_MS = 60_000;
const COLUMNS_LS_KEY = 'portal.sites.columns.v1';
const SORT_LS_KEY = 'portal.sites.sort.v1';

const sites = ref<PortalSite[]>([]);
const loading = ref(true);
const filter = ref('');
const error = ref<string | null>(null);
const router = useRouter();
const userStore = useUserStore();
const isInternal = computed(() => userStore.identity?.isInternal ?? false);

type ColumnKey =
    | 'site'
    | 'status'
    | 'connectedSince'
    | 'lastSeen'
    | 'ip'
    | 'agentVersion'
    | 'shortcuts';

interface ColumnDef {
    key: ColumnKey;
    label: string;
    sortable: boolean;
    defaultVisible: boolean;
    // When true, the column is hidden entirely (not even in the picker) for
    // non-internal users. Currently only `agentVersion` — see decisions.md.
    internalOnly?: boolean;
    // Whether the user can hide this column from the picker. Site is always
    // shown — hiding the name would leave a row that is essentially blank.
    alwaysVisible?: boolean;
    align?: 'left' | 'right' | 'center';
}

const COLUMNS: ColumnDef[] = [
    { key: 'site',           label: 'Site',           sortable: true,  defaultVisible: true, alwaysVisible: true },
    { key: 'status',         label: 'Status',         sortable: true,  defaultVisible: true },
    { key: 'connectedSince', label: 'Connected since', sortable: true, defaultVisible: true },
    { key: 'lastSeen',       label: 'Last seen',      sortable: true,  defaultVisible: false },
    { key: 'ip',             label: 'IP',             sortable: true,  defaultVisible: true },
    { key: 'agentVersion',   label: 'Agent version',  sortable: true,  defaultVisible: false, internalOnly: true },
    { key: 'shortcuts',      label: 'Quick connect',  sortable: false, defaultVisible: true, align: 'right' },
];

// Hide internal-only columns from the column set entirely for external
// customers — they should never appear in the picker.
const availableColumns = computed(() =>
    COLUMNS.filter((c) => !c.internalOnly || isInternal.value),
);

const visibility = ref<Record<ColumnKey, boolean>>(loadVisibility());

function defaultVisibility(): Record<ColumnKey, boolean> {
    const out = {} as Record<ColumnKey, boolean>;
    for (const c of COLUMNS) out[c.key] = c.defaultVisible;
    return out;
}

function loadVisibility(): Record<ColumnKey, boolean> {
    const fallback = defaultVisibility();
    try {
        const raw = localStorage.getItem(COLUMNS_LS_KEY);
        if (!raw) return fallback;
        const parsed = JSON.parse(raw) as Partial<Record<ColumnKey, boolean>>;
        // Merge known keys; ignore unknown keys from older versions so a
        // future schema change doesn't error here.
        for (const c of COLUMNS) {
            if (typeof parsed[c.key] === 'boolean') fallback[c.key] = parsed[c.key] as boolean;
        }
        return fallback;
    } catch { return fallback; }
}

watch(visibility, (v) => {
    try { localStorage.setItem(COLUMNS_LS_KEY, JSON.stringify(v)); } catch { /* quota/disabled */ }
}, { deep: true });

const visibleColumns = computed(() =>
    availableColumns.value.filter((c) => c.alwaysVisible || visibility.value[c.key]),
);

// Sorting -------------------------------------------------------------------

type SortDir = 'asc' | 'desc';
interface SortState { key: ColumnKey | null; dir: SortDir }

const sort = ref<SortState>(loadSort());

function loadSort(): SortState {
    try {
        const raw = localStorage.getItem(SORT_LS_KEY);
        if (!raw) return { key: null, dir: 'asc' };
        const parsed = JSON.parse(raw) as Partial<SortState>;
        const key = COLUMNS.find((c) => c.key === parsed.key && c.sortable)?.key ?? null;
        const dir: SortDir = parsed.dir === 'desc' ? 'desc' : 'asc';
        return { key, dir };
    } catch { return { key: null, dir: 'asc' }; }
}

watch(sort, (v) => {
    try { localStorage.setItem(SORT_LS_KEY, JSON.stringify(v)); } catch { /* quota/disabled */ }
}, { deep: true });

function toggleSort(key: ColumnKey) {
    const col = COLUMNS.find((c) => c.key === key);
    if (!col?.sortable) return;
    if (sort.value.key !== key) { sort.value = { key, dir: 'asc' }; return; }
    if (sort.value.dir === 'asc') { sort.value = { key, dir: 'desc' }; return; }
    sort.value = { key: null, dir: 'asc' };
}

// Sort accessors. nulls always sort to the bottom regardless of direction so
// "missing data" rows don't move around as the user flips asc/desc.
function ipKey(ip: string | null | undefined): number {
    if (!ip) return Number.POSITIVE_INFINITY;
    const parts = ip.split('.').map((p) => parseInt(p, 10));
    if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return Number.POSITIVE_INFINITY;
    return ((parts[0] * 256 + parts[1]) * 256 + parts[2]) * 256 + parts[3];
}

function sortValue(s: PortalSite, key: ColumnKey): string | number | null {
    switch (key) {
        case 'site':   return s.name.toLowerCase();
        case 'status': return s.online ? 0 : 1;
        case 'connectedSince': return s.online && s.lastSeen ? Date.parse(s.lastSeen) : null;
        case 'lastSeen':       return !s.online && s.lastSeen ? Date.parse(s.lastSeen) : null;
        case 'ip':     return ipKey(s.ip);
        case 'agentVersion': return (s.extended?.agentVersion ?? '').toLowerCase();
        default: return null;
    }
}

const filteredAndSorted = computed(() => {
    const f = filter.value.toLowerCase();
    const base = !f ? sites.value : sites.value.filter((s) =>
        s.name.toLowerCase().includes(f) ||
        s.siteId.toLowerCase().includes(f) ||
        (s.ip ?? '').toLowerCase().includes(f),
    );

    if (!sort.value.key) return base;
    const key = sort.value.key;
    const mult = sort.value.dir === 'asc' ? 1 : -1;
    const copy = base.slice();
    copy.sort((a, b) => {
        const va = sortValue(a, key);
        const vb = sortValue(b, key);
        // Push null/missing to the bottom regardless of direction.
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        if (va < vb) return -1 * mult;
        if (va > vb) return  1 * mult;
        return 0;
    });
    return copy;
});

const counts = computed(() => ({
    total: sites.value.length,
    online: sites.value.filter((s) => s.online).length,
    filtered: filteredAndSorted.value.length,
}));

// Data loading -------------------------------------------------------------

async function load(opts: { background?: boolean } = {}) {
    if (!opts.background) loading.value = true;
    error.value = null;
    try { sites.value = await api<PortalSite[]>('/api/sites'); }
    catch (e) { error.value = (e as Error).message; }
    finally { loading.value = false; }
}

let timer: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
    load();
    timer = setInterval(() => { load({ background: true }); }, AUTO_REFRESH_MS);
});
onBeforeUnmount(() => { if (timer) { clearInterval(timer); timer = null; } });

// Column picker dropdown --------------------------------------------------

const pickerOpen = ref(false);
function togglePicker() { pickerOpen.value = !pickerOpen.value; }
function closePicker(e: MouseEvent) {
    const t = e.target as HTMLElement;
    if (!t.closest('[data-column-picker]')) pickerOpen.value = false;
}
onMounted(() => { document.addEventListener('click', closePicker); });
onBeforeUnmount(() => { document.removeEventListener('click', closePicker); });

// Row actions --------------------------------------------------------------

function openSite(s: PortalSite) {
    router.push(`/sites/${encodeURIComponent(s.siteId)}`);
}

const copiedSiteId = ref<string | null>(null);
async function copyIp(ev: Event, s: PortalSite) {
    ev.stopPropagation();
    if (!s.ip) return;
    try {
        await navigator.clipboard.writeText(s.ip);
        copiedSiteId.value = s.siteId;
        setTimeout(() => {
            if (copiedSiteId.value === s.siteId) copiedSiteId.value = null;
        }, 1500);
    } catch { /* clipboard unavailable */ }
}
function stop(e: Event) { e.stopPropagation(); }
function downloadRdp(ev: Event, s: PortalSite) {
    ev.stopPropagation();
    openRdp(s.ip, s.name);
}
</script>

<template>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h1 class="text-2xl font-semibold h-9 inline-flex items-baseline gap-3 flex-wrap">
                <span>Your sites</span>
                <span v-if="!loading && !error" class="text-xs font-normal text-bp-muted">
                    <template v-if="counts.filtered !== counts.total">
                        <span class="text-bp-text">{{ counts.filtered }}</span> shown ·
                    </template>
                    <span class="text-bp-text">{{ counts.total }}</span> total ·
                    <span class="text-emerald-400">{{ counts.online }}</span> online
                </span>
            </h1>
            <div class="flex items-center gap-3 w-full sm:w-auto">
                <input v-model="filter" placeholder="Filter name, id or IP…"
                       class="bg-bp-panel border border-bp-border rounded px-3 py-1.5 text-sm flex-1 sm:flex-none sm:w-64
                              focus:outline-none focus:ring-1 focus:ring-bp-accent" />
                <button @click="load()" class="text-bp-muted hover:text-bp-link-hover text-sm whitespace-nowrap">Refresh</button>

                <!-- Column settings — hidden on phones where the cards have no
                     column structure to configure. -->
                <div class="relative hidden md:block" data-column-picker>
                    <button
                        type="button"
                        @click.stop="togglePicker"
                        title="Column settings"
                        class="p-1.5 rounded text-bp-muted hover:text-bp-text hover:bg-bp-panel-2 focus:outline-none">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                            class="w-5 h-5">
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </svg>
                    </button>
                    <div
                        v-if="pickerOpen"
                        class="absolute right-0 mt-2 w-56 bg-bp-panel border border-bp-border rounded
                               shadow-2xl z-10 p-2 text-sm text-left">
                        <p class="text-xs text-bp-muted uppercase tracking-wide px-2 pt-1 pb-2">Show columns</p>
                        <label
                            v-for="c in availableColumns"
                            :key="c.key"
                            class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-bp-panel-2 cursor-pointer"
                            :class="{ 'opacity-60 cursor-not-allowed': c.alwaysVisible }">
                            <input
                                type="checkbox"
                                :checked="c.alwaysVisible ? true : visibility[c.key]"
                                :disabled="c.alwaysVisible"
                                @change="visibility[c.key] = ($event.target as HTMLInputElement).checked"
                                class="accent-bp-accent" />
                            <span>{{ c.label }}</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="error" class="bg-red-900/40 border border-red-700 text-red-200 px-3 py-2 rounded mb-4 text-sm">
            {{ error }}
        </div>
        <div v-if="loading" class="text-slate-400 text-sm">Loading…</div>

        <!-- Desktop / tablet: full table with sortable headers + column picker.
             Hidden on phones because horizontally scrolling 5-7 columns inside
             a 360px viewport is a bad experience. -->
        <div v-else class="hidden md:block bg-bp-panel border border-bp-border rounded overflow-hidden">
            <table class="w-full text-sm">
                <thead class="bg-slate-800/50 text-xs text-slate-400 uppercase tracking-wide">
                    <tr>
                        <th
                            v-for="c in visibleColumns"
                            :key="c.key"
                            scope="col"
                            class="py-2 px-4"
                            :class="[
                                c.align === 'right' ? 'text-right' : 'text-left',
                                c.sortable ? 'cursor-pointer select-none hover:text-bp-text' : '',
                            ]"
                            @click="c.sortable ? toggleSort(c.key) : null">
                            <span class="inline-flex items-center gap-1">
                                {{ c.label }}
                                <template v-if="c.sortable">
                                    <span
                                        v-if="sort.key === c.key"
                                        class="text-bp-link"
                                    >{{ sort.dir === 'asc' ? '▲' : '▼' }}</span>
                                    <span v-else class="text-slate-600">▲</span>
                                </template>
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-800">
                    <tr v-for="s in filteredAndSorted" :key="s.siteId"
                        class="hover:bg-slate-800/40 cursor-pointer"
                        @click="openSite(s)">
                        <td
                            v-for="c in visibleColumns"
                            :key="c.key"
                            class="py-2 px-4 align-top"
                            :class="c.align === 'right' ? 'text-right' : ''">
                            <template v-if="c.key === 'site'">
                                <div class="font-medium">{{ s.name }}</div>
                            </template>
                            <template v-else-if="c.key === 'status'">
                                <SiteStatusBadge :online="s.online" />
                            </template>
                            <template v-else-if="c.key === 'connectedSince'">
                                <TimeAgo v-if="s.online" :iso="s.lastSeen" />
                                <span v-else class="text-slate-500">—</span>
                            </template>
                            <template v-else-if="c.key === 'lastSeen'">
                                <TimeAgo v-if="!s.online" :iso="s.lastSeen" />
                                <span v-else class="text-slate-500">—</span>
                            </template>
                            <template v-else-if="c.key === 'ip'">
                                <span class="font-mono">{{ s.ip || '—' }}</span>
                            </template>
                            <template v-else-if="c.key === 'agentVersion'">
                                <span class="font-mono text-xs">{{ s.extended?.agentVersion || '—' }}</span>
                            </template>
                            <template v-else-if="c.key === 'shortcuts'">
                                <div v-if="s.ip" class="flex items-center justify-end gap-1.5">
                                    <button
                                        type="button"
                                        @click="downloadRdp($event, s)"
                                        :title="`Open RDP to ${s.ip}`"
                                        class="p-1.5 rounded text-bp-muted hover:text-bp-link-hover hover:bg-bp-panel-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
                                            <path d="M2 4.5A1.5 1.5 0 013.5 3h13A1.5 1.5 0 0118 4.5v8a1.5 1.5 0 01-1.5 1.5H11v1.5h2a.5.5 0 010 1H7a.5.5 0 010-1h2V14H3.5A1.5 1.5 0 012 12.5v-8zM3.5 4a.5.5 0 00-.5.5v8a.5.5 0 00.5.5h13a.5.5 0 00.5-.5v-8a.5.5 0 00-.5-.5h-13z" />
                                        </svg>
                                    </button>
                                    <a
                                        :href="`https://${s.ip}/admin`"
                                        target="_blank"
                                        rel="noopener"
                                        @click="stop"
                                        :title="`Open https://${s.ip}/admin`"
                                        class="p-1.5 rounded text-bp-muted hover:text-bp-link-hover hover:bg-bp-panel-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                                            class="w-4 h-4">
                                            <circle cx="12" cy="12" r="9" />
                                            <path d="M3 12h18" />
                                            <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
                                        </svg>
                                    </a>
                                    <button
                                        type="button"
                                        @click="copyIp($event, s)"
                                        :title="copiedSiteId === s.siteId ? 'Copied' : `Copy ${s.ip}`"
                                        class="p-1.5 rounded text-bp-muted hover:text-bp-link-hover hover:bg-bp-panel-2">
                                        <svg
                                            v-if="copiedSiteId === s.siteId"
                                            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                            fill="currentColor" class="w-4 h-4 text-emerald-400">
                                            <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z" clip-rule="evenodd" />
                                        </svg>
                                        <svg
                                            v-else
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                                            class="w-4 h-4">
                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                        </svg>
                                    </button>
                                </div>
                                <span v-else class="text-slate-500">—</span>
                            </template>
                        </td>
                    </tr>
                    <tr v-if="!filteredAndSorted.length">
                        <td :colspan="visibleColumns.length" class="py-8 px-4 text-center text-slate-500 text-sm">
                            No sites visible.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Mobile: stacked cards. Two rows only — name, then status + IP +
             quick-connect icons. Connected-since / last-seen / agent-version
             live on SiteDetail. The filter input above is the way to narrow
             the list down (no click-to-sort here). -->
        <div v-if="!loading" class="md:hidden space-y-2">
            <div
                v-for="s in filteredAndSorted"
                :key="s.siteId"
                role="link"
                tabindex="0"
                class="bg-bp-panel border border-bp-border rounded p-3 hover:bg-slate-800/40 active:bg-slate-800/40 cursor-pointer"
                @click="openSite(s)"
                @keydown.enter="openSite(s)"
                @keydown.space.prevent="openSite(s)">
                <div class="font-medium truncate">{{ s.name }}</div>
                <div class="mt-1.5 flex items-center gap-2">
                    <SiteStatusBadge :online="s.online" />
                    <span v-if="s.ip" class="font-mono text-xs text-bp-muted truncate min-w-0">{{ s.ip }}</span>
                    <div v-if="s.ip" class="ml-auto flex items-center gap-1 flex-shrink-0">
                        <button
                            type="button"
                            @click="downloadRdp($event, s)"
                            :title="`Open RDP to ${s.ip}`"
                            class="p-1.5 rounded text-bp-muted hover:text-bp-link-hover active:bg-bp-panel-2">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                                <path d="M2 4.5A1.5 1.5 0 013.5 3h13A1.5 1.5 0 0118 4.5v8a1.5 1.5 0 01-1.5 1.5H11v1.5h2a.5.5 0 010 1H7a.5.5 0 010-1h2V14H3.5A1.5 1.5 0 012 12.5v-8zM3.5 4a.5.5 0 00-.5.5v8a.5.5 0 00.5.5h13a.5.5 0 00.5-.5v-8a.5.5 0 00-.5-.5h-13z" />
                            </svg>
                        </button>
                        <a
                            :href="`https://${s.ip}/admin`"
                            target="_blank"
                            rel="noopener"
                            @click="stop"
                            :title="`Open https://${s.ip}/admin`"
                            class="p-1.5 rounded text-bp-muted hover:text-bp-link-hover active:bg-bp-panel-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                                class="w-5 h-5">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M3 12h18" />
                                <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
                            </svg>
                        </a>
                        <button
                            type="button"
                            @click="copyIp($event, s)"
                            :title="copiedSiteId === s.siteId ? 'Copied' : `Copy ${s.ip}`"
                            class="p-1.5 rounded text-bp-muted hover:text-bp-link-hover active:bg-bp-panel-2">
                            <svg
                                v-if="copiedSiteId === s.siteId"
                                xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                fill="currentColor" class="w-5 h-5 text-emerald-400">
                                <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z" clip-rule="evenodd" />
                            </svg>
                            <svg
                                v-else
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                                class="w-5 h-5">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="!filteredAndSorted.length"
                 class="bg-bp-panel border border-bp-border rounded py-8 px-4 text-center text-slate-500 text-sm">
                No sites visible.
            </div>
        </div>
    </div>
</template>
